import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FieldProps } from 'src/modules/common/components/DecimalField/types';
import { DecimalInput } from 'src/modules/common/components/DecimalField/DecimalInput';
import { ConfigContext } from 'src/configContext.ts';
import { formatNumericString } from 'src/utils/formatNumericString.ts';

export const DecimalField: (props: FieldProps) => React.ReactElement = ({
    name,
    value,
    onValueChange,
    labelText,
    currentLocale,
    maxValue,
    ...restProps
}) => {
    const {
        numberFormat: { decimalSeparator },
    } = useContext(ConfigContext);
    const [formattedValue, setFormattedValue] = useState('');

    const localizedToNumeric = useCallback(
        (localized: string) => localized
            .replace(new RegExp(`\\${decimalSeparator}|,`, 'g'), '.'),
        [decimalSeparator],
    );

    const onChangeHandle: React.ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
        const maximum = maxValue || Number.MAX_SAFE_INTEGER;

        const inputValue = e.target.value;
        const numeric = localizedToNumeric(inputValue);
        const formattedString = formatNumericString(numeric);

        // added leading zero if only dot is entered
        if (numeric === '.') {
            setFormattedValue(`0${decimalSeparator}`);
            return;
        }

        // validation failed
        if (formattedString === null) {
            return;
        }

        const lastCharacter = inputValue.slice(-1);

        // cover the case of incomplete decimal, when the user only put the dot
        if (lastCharacter === decimalSeparator || lastCharacter === ',' || lastCharacter === '.') {
            const head = numeric.slice(0, -1);
            const incompleteFormatted = `${head}${decimalSeparator}`;
            setFormattedValue(incompleteFormatted);
            return;
        }

        const formattedStringLimited = Number(formattedString) > maximum ? String(maximum) : formattedString;

        if (formattedStringLimited !== value) {
            onValueChange(formattedStringLimited);
        }

        setFormattedValue(formattedStringLimited);
    }, [onValueChange, value, localizedToNumeric, decimalSeparator, maxValue]);

    useEffect(() => {
        const formattedString = formatNumericString(value);

        if (formattedString !== null) {
            setFormattedValue(formattedString);
        }
    }, [value]);

    return (
        <DecimalInput
            {...restProps}
            type="text"
            id={name}
            name={name}
            value={formattedValue}
            onChange={onChangeHandle}
            labelText={labelText}
        />
    );
};
